import {
    useQuery,
    useMutation
} from "@tanstack/react-query";
import axios from "@/axios.config";
import { ApiResponse } from "@/types/fetch.type";
import { StudyLevelConfiguration } from "@/types";
import { AxiosError } from "axios";

export const useGetAllStudyLevelConfigurations = () => {
    return useQuery<ApiResponse<StudyLevelConfiguration[]>, AxiosError<{ message: string }>>({
        queryKey: ["study-level-configurations"],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<StudyLevelConfiguration[]>>("/tblstudylevelconfiguration");
            return response.data;
        },
    });
}

export const useGetStudyLevelConfigurationByCouncil = (councilId: string) => {
    return useQuery<ApiResponse<StudyLevelConfiguration[]>, AxiosError<{ message: string }>>({
        queryKey: ["study-level-configuration", councilId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<StudyLevelConfiguration[]>>(`/tblstudylevelconfiguration/council/${councilId}`);
            return response.data;
        },
        enabled: !!councilId,
    });
}

export const useGetStudyLevelConfigurationByCouncilAndService = (params: {
    councilId: string;
    serviceId: string;
}) => {
    return useQuery<ApiResponse<StudyLevelConfiguration>, AxiosError<{ message: string }>>({
        queryKey: ["study-level-configuration", params.councilId, params.serviceId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<StudyLevelConfiguration>>(`/tblstudylevelconfiguration/by-council-and-service?councilId=${params.councilId}&serviceId=${params.serviceId}`,);
            return response.data;
        },
        enabled: !!params.councilId && !!params.serviceId,
    });
}

export interface CreateStudyLevelConfigurationParams {
    studyLevelName: string;
    councilId: string;
    serviceId: string;
}

export const useCreateStudyLevelConfiguration = () => {
    return useMutation<ApiResponse<StudyLevelConfiguration>, AxiosError<{ message: string }>, CreateStudyLevelConfigurationParams>({
        mutationFn: async (params) => {
            const response = await axios.post<ApiResponse<StudyLevelConfiguration>>("/tblstudylevelconfiguration", params);
            return response.data;
        },
    });
}

export interface UpdateStudyLevelConfigurationParams {
    id: string;
    studyLevelName: string;
    councilId: string;
    serviceId: string;
}

export const useUpdateStudyLevelConfiguration = () => {
    return useMutation<ApiResponse<StudyLevelConfiguration>, AxiosError<{ message: string }>, UpdateStudyLevelConfigurationParams>({
        mutationFn: async (params) => {
            const response = await axios.put<ApiResponse<StudyLevelConfiguration>>(`/tblstudylevelconfiguration/${params.id}`, params);
            return response.data;
        },
    });
}

export const useDeleteStudyLevelConfiguration = () => {
    return useMutation<ApiResponse<StudyLevelConfiguration>, AxiosError<{ message: string }>, string>({
        mutationFn: async (id) => {
            const response = await axios.delete<ApiResponse<StudyLevelConfiguration>>(`/tblstudylevelconfiguration/${id}`);
            return response.data;
        },
    });
}
